import React from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Space,
  Switch,
  Upload,
  message,
  Spin,
} from "antd";
import type { UploadFile } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import TinyMCEEditor from "../../components/TinyMCEEditor";
import { CultureModalProps } from "../../types/culture";
import cultureService from "../../services/cultureService";
import { IMAGE_UPLOAD_ACCEPT, validateImageFile } from "../../utils/upload";

// Giá trị field `thumbnail`: mảng URL (dữ liệu từ API) hoặc UploadFile (sau khi thêm/xoá ảnh)
type ThumbnailItem = string | UploadFile;

const toUploadFileList = (value?: ThumbnailItem[] | null): UploadFile[] =>
  (value ?? []).map((item, index) =>
    typeof item === "string"
      ? {
          uid: `thumbnail-${index}`,
          name: item.split("/").pop() || `image-${index + 1}`,
          status: "done",
          url: item,
        }
      : item
  );

const uploadImage = (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileCategory", "blog");
  return cultureService.uploadImage(formData);
};

const CultureModal: React.FC<CultureModalProps> = ({
  visible,
  form,
  onFinish,
  onCancel,
  editingRecord,
  isModalLoading,
  setIsModalLoading,
}) => {
  const handleEditorChange = (content: string) => {
    form.setFieldsValue({ content: content });
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsModalLoading(true);

      // API nhận 1 file mỗi request và lưu `thumbnail` là mảng URL:
      // upload lần lượt các ảnh mới, giữ ảnh cũ và thứ tự trong danh sách
      const thumbnail: string[] = [];
      for (const file of toUploadFileList(values.thumbnail)) {
        if (file.originFileObj) {
          thumbnail.push(await uploadImage(file.originFileObj));
        } else if (file.url) {
          thumbnail.push(file.url);
        }
      }

      onFinish({ ...values, thumbnail });
    } catch (error) {
      console.error("Lỗi khi lưu bài đăng: ", error);
      message.error("Lỗi khi lưu bài đăng: " + error);
      setIsModalLoading(false);
    }
  };

  return (
    <Modal
      title={editingRecord ? "Chỉnh sửa bài đăng" : "Thêm bài đăng"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Spin spinning={isModalLoading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[{ required: true, message: "Tiêu đề không được để trống" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="thumbnail"
            label="Hình ảnh"
            initialValue={[]}
            getValueProps={(value?: ThumbnailItem[] | null) => ({
              fileList: toUploadFileList(value),
            })}
            getValueFromEvent={(event: { fileList: UploadFile[] }) =>
              event.fileList
            }
            rules={[
              {
                type: "array",
                required: true,
                message: "Cần ít nhất 1 hình ảnh",
              },
            ]}
          >
            <Upload
              listType="picture-card"
              multiple
              accept={IMAGE_UPLOAD_ACCEPT}
              beforeUpload={(file) =>
                // Chỉ thêm vào danh sách; ảnh được upload khi bấm Lưu
                validateImageFile(file) ? false : Upload.LIST_IGNORE
              }
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Thêm ảnh</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="published"
            label="Trạng thái"
            initialValue={editingRecord?.published || true}
          >
            <Switch
              defaultChecked
              checkedChildren="Hiển thị"
              unCheckedChildren="Ẩn"
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Nội dung"
            rules={[{ required: true, message: "Nội dung không được để trống" }]}
          >
            <TinyMCEEditor
              apiKey={process.env.REACT_APP_EDITOR_KEY}
              initialValue={editingRecord?.content || ""}
              onChange={handleEditorChange}
              height={300}
              placeholder="Nội dung"
              type="blog"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Lưu
              </Button>
              <Button onClick={onCancel}>Huỷ</Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CultureModal;
